/**
 * EFFET : page « Vérifier ma caméra et ma connexion » (#/diagnostic) — utile avant la
 * première partie, ou pour comprendre pourquoi ça ne marche pas chez un ami.
 */
import { iceConfig, turnServers } from '../config';
import {
  analysisVerdict,
  compareDelegates,
  type AnalysisHealth,
  type AnalysisMeasure,
} from '../diag/analysis';
import {
  DEFAULT_DELEGATE,
  DELEGATES,
  type Delegate,
  type DelegateMeasure,
} from '../detection/delegate';
import { rememberDelegate } from '../detection/delegatePreference';
import type { FaceSource } from '../detection/faceSource';
import { networkVerdict, summarizeCandidates } from '../diag/candidates';
import { openCamera, type CameraFaceSource, type FaceSourceHealth } from '../detection/faceTracker';
import { createLocalFaceSource } from '../detection/localFaceSource';
import { mergeIceServers } from '../net/turn';
import { h, mount } from './dom';

/** Mesure de la cadence, après le démarrage de MediaPipe et un temps de stabilisation. */
const ANALYSIS_MS = 3000;
const SETTLE_MS = 1000;
const START_TIMEOUT_MS = 20_000;
const GATHER_MS = 6000;

type Level = 'pending' | 'ok' | 'warn' | 'error';

/** Processeur graphique vu par WebGL 2 (celui qu'utilise MediaPipe) ; `null` si inconnu. */
function gpuRenderer(): string | null {
  try {
    const gl = document.createElement('canvas').getContext('webgl2');
    if (!gl) return 'WebGL 2 indisponible';
    const info = gl.getExtension('WEBGL_debug_renderer_info');
    return String(gl.getParameter(info ? info.UNMASKED_RENDERER_WEBGL : gl.RENDERER));
  } catch {
    return null;
  }
}

/**
 * Images livrées par la caméra jusqu'ici, comptées par le navigateur : juste même quand la
 * page est trop occupée (analyse) pour être prévenue de chaque image.
 */
function presentedFrames(video: HTMLVideoElement): number {
  return video.getVideoPlaybackQuality().totalVideoFrames;
}

function toAnalysisHealth(h: FaceSourceHealth, video: HTMLVideoElement): AnalysisHealth | null {
  const { loop } = h;
  return (
    loop && {
      delegate: h.delegate,
      worker: h.where === 'worker',
      mode: loop.mode,
      stalled: loop.stalled,
      errors: loop.errors + h.analysisErrors,
      restarts: h.restarts,
      lastError: h.lastError,
      frames: loop.frames,
      started: h.started,
      completed: h.completed,
      timeouts: h.timeouts,
      resumes: loop.resumes,
      video: describeVideo(video),
    }
  );
}

/** État de l'élément vidéo caché et de sa piste caméra, pour le détail du diagnostic. */
function describeVideo(video: HTMLVideoElement): string {
  const track = video.srcObject instanceof MediaStream ? video.srcObject.getVideoTracks()[0] : null;
  const state = video.paused ? 'EN PAUSE' : video.ended ? 'terminée' : 'lecture';
  const piste = track
    ? `piste ${track.readyState}${track.muted ? ' muette' : ''}${track.enabled ? '' : ' désactivée'}`
    : 'pas de piste';
  return `${state} ${video.videoWidth}×${video.videoHeight}, readyState ${video.readyState}, ${piste}`;
}

const delegateName = (d: Delegate): string => (d === 'GPU' ? 'le GPU' : 'le processeur');

function section(title: string, testid: string) {
  const status = h('p', { class: 'diag-status', role: 'status' }, 'Vérification…');
  const detail = h('p', { class: 'muted' });
  const extra = h('p', { class: 'muted', testid: `${testid}-extra` });
  const element = h(
    'section',
    { class: 'card diag', testid },
    h('h2', {}, title),
    status,
    detail,
    extra,
  );
  element.dataset['state'] = 'pending';
  extra.hidden = true;
  return {
    element,
    set(level: Level, text: string, more = '', comparison = '') {
      element.dataset['state'] = level;
      status.textContent = `${{ pending: '…', ok: '✅', warn: '⚠️', error: '❌' }[level]} ${text}`;
      detail.textContent = more;
      extra.textContent = comparison;
      extra.hidden = comparison === '';
    },
  };
}

export function renderDiagnostic(root: HTMLElement, onHome: () => void): () => void {
  const camera = section('Caméra', 'diag-camera');
  const analysis = section('Analyse du visage', 'diag-analysis');
  const network = section('Réseau', 'diag-network');
  const video = h('video', { autoplay: '', muted: '', playsinline: '', class: 'offscreen-video' });
  video.muted = true;
  const back = h('button', { type: 'button', class: 'button secondary' }, '← Accueil');
  back.addEventListener('click', onHome);
  mount(
    root,
    h('h1', { class: 'title' }, 'Diagnostic'),
    h(
      'p',
      { class: 'muted' },
      'Tout reste dans ton navigateur : rien n’est envoyé, sauf une demande de relais réseau.',
    ),
    camera.element,
    analysis.element,
    network.element,
    h('div', { class: 'actions' }, back),
    video,
  );

  let stream: MediaStream | null = null;
  const state = { stopped: false };
  /** Relu après chaque attente (la page a pu être quittée entre-temps). */
  const stopped = (): boolean => state.stopped;
  const cleanups: (() => void)[] = [];

  const checkCamera = async (): Promise<boolean> => {
    try {
      stream = await openCamera();
      video.srcObject = stream;
      const s = stream.getVideoTracks()[0]?.getSettings();
      camera.set('ok', 'Caméra disponible', s?.width ? `${s.width} × ${s.height ?? '?'} px` : '');
      return true;
    } catch (e) {
      camera.set('error', 'Caméra indisponible', e instanceof Error ? e.message : String(e));
      analysis.set('error', 'Impossible sans caméra');
      return false;
    }
  };

  interface Run {
    readonly frames: number;
    readonly faces: number;
    readonly cameraFrames: number;
    readonly warmupMs: number;
    /** Relevés AVANT l'arrêt de la source (la boucle disparaît avec elle). */
    readonly health: AnalysisHealth | null;
    readonly avgMs: number | null;
    readonly inferenceMs: number | null;
  }

  /** Démarre la source, laisse passer le démarrage, mesure `ANALYSIS_MS` ; `null` : pas démarrée. */
  const measure = async (source: FaceSource): Promise<Run | null> => {
    let frames = 0;
    let faces = 0;
    let firstFrame: () => void = () => undefined;
    const started = new Promise<void>((resolve) => {
      firstFrame = resolve;
    });
    const off = source.subscribe((f) => {
      frames++;
      if (f.blendshapes) faces++;
      firstFrame();
    });
    cleanups.push(off, () => {
      if ('close' in source) (source as CameraFaceSource).close();
      else source.stop();
    });
    // Le démarrage (chargement du modèle, premières images très lentes) n'est pas mesuré.
    const t0 = performance.now();
    try {
      await source.start();
    } catch (e) {
      console.warn('Analyse du visage : démarrage impossible', e);
      return null;
    }
    const ready = await Promise.race([
      started.then(() => true),
      new Promise<boolean>((r) => {
        setTimeout(() => {
          r(false);
        }, START_TIMEOUT_MS);
      }),
    ]);
    if (!ready || stopped()) return null;
    const warmupMs = performance.now() - t0;
    await new Promise((r) => setTimeout(r, SETTLE_MS));
    const camera = 'health' in source ? (source as CameraFaceSource) : null;
    const cameraStart = presentedFrames(video);
    const loopStart = camera?.health().loop?.frames ?? 0;
    frames = 0;
    faces = 0;
    await new Promise((r) => setTimeout(r, ANALYSIS_MS));
    const health = camera && toAnalysisHealth(camera.health(), video);
    // Firefox ne compte pas les images affichées d'une caméra : les images reçues par la
    // boucle font foi quand elles sont plus nombreuses.
    const cameraFrames = Math.max(
      presentedFrames(video) - cameraStart,
      (health?.frames ?? 0) - loopStart,
    );
    const stats = camera?.stats();
    const avgMs = stats?.avgMs ?? null;
    const inferenceMs = stats?.inferenceMs ?? null;
    off();
    // Déchargé (worker compris) : la mesure suivante repart de zéro, sans concurrence.
    if (camera) camera.close();
    else source.stop();
    return { frames, faces, cameraFrames, warmupMs, health, avgMs, inferenceMs };
  };

  /**
   * Webcam : MediaPipe est mesuré sur le GPU puis sur le processeur ; le plus rapide est
   * mémorisé pour les jeux. Visage synthétique (e2e) : une seule mesure.
   */
  const checkAnalysis = async (): Promise<void> => {
    const seconds = ANALYSIS_MS / 1000;
    const first = createLocalFaceSource(video, DELEGATES[0]);
    const isCamera = 'health' in first;
    const candidates: { delegate: Delegate | null; source: FaceSource }[] = isCamera
      ? DELEGATES.map((delegate, i) => ({
          delegate,
          source: i === 0 ? first : createLocalFaceSource(video, delegate),
        }))
      : [{ delegate: null, source: first }];
    const results: { measure: DelegateMeasure; input: AnalysisMeasure }[] = [];
    for (const { delegate, source } of candidates) {
      if (delegate) analysis.set('pending', `Mesure de MediaPipe sur ${delegateName(delegate)}…`);
      const run = await measure(source);
      if (stopped()) return;
      const health = run?.health ?? null;
      const analysisFps = run ? run.frames / seconds : 0;
      const avgMs = run?.avgMs ?? null;
      const inferenceMs = run?.inferenceMs ?? null;
      results.push({
        measure: {
          delegate: delegate ?? DEFAULT_DELEGATE,
          analysisFps,
          avgMs,
          errors: (health?.errors ?? 0) + (run ? 0 : 1),
        },
        input: {
          cameraFps: run ? run.cameraFrames / seconds : 0,
          analysisFps,
          avgMs,
          inferenceMs,
          faceRatio: run && run.frames > 0 ? run.faces / run.frames : 0,
          warmupMs: run?.warmupMs ?? 0,
          renderer: gpuRenderer(),
          health,
        },
      });
    }
    const chosenDelegate = isCamera ? compareDelegates(results.map((r) => r.measure)) : null;
    const chosen = results.find((r) => r.measure.delegate === chosenDelegate?.choice) ?? results[0];
    if (!chosen) return;
    if (chosen.input.warmupMs === 0) {
      analysis.set('error', "L'analyse du visage ne démarre pas", 'Recharge la page.');
      return;
    }
    if (chosenDelegate) rememberDelegate(chosenDelegate.choice);
    const verdict = analysisVerdict(chosen.input);
    analysis.set(verdict.level, verdict.message, verdict.detail, chosenDelegate?.text ?? '');
  };

  const checkNetwork = async (): Promise<void> => {
    const turn = await turnServers();
    const pc = new RTCPeerConnection({
      iceServers: mergeIceServers(iceConfig.iceServers, turn),
    });
    cleanups.push(() => {
      pc.close();
    });
    const candidates: string[] = [];
    pc.createDataChannel('diag');
    const done = new Promise<void>((resolve) => {
      pc.addEventListener('icecandidate', (e) => {
        if (e.candidate) candidates.push(e.candidate.candidate);
        else resolve();
      });
      setTimeout(resolve, GATHER_MS);
    });
    await pc.setLocalDescription(await pc.createOffer());
    await done;
    if (state.stopped) return;
    const summary = summarizeCandidates(candidates);
    const verdict = networkVerdict(summary);
    network.element.dataset['relay'] = String(summary.relay);
    network.set(
      verdict.level,
      verdict.message,
      `Relais TURN ${turn.length > 0 ? 'configuré' : 'non configuré'} · chemins trouvés : local ${summary.host}, STUN ${summary.srflx}, relais ${summary.relay}`,
    );
  };

  void (async () => {
    const [cameraOk] = await Promise.all([checkCamera(), checkNetwork()]);
    if (cameraOk && !state.stopped) await checkAnalysis();
  })();

  return () => {
    state.stopped = true;
    for (const c of cleanups) c();
    for (const t of stream?.getTracks() ?? []) t.stop();
  };
}
